import { rem } from 'polished'
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import Pane from 'components/Common/Pane'
import CurrencyContext from 'contexts/currencyContext'
import ExperienceTicketSelection from '../ExperienceTickets/ExperienceTicketSelection'
import ExperienceTicketInputs from '../ExperienceTickets/ExperienceTicketInputs'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ExperienceDetailsForm } from '../ExperienceDetailsModal/ExperienceDetailsBookingModal'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject, min, sum, max } from 'lib/array/arrayUtils'
import cn from 'clsx'
import { generateExperienceCheckoutItem } from 'lib/checkout/experiences/cart'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import ExperiencePageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import ExperiencePageDispatchContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageDispatchContext'
import { ExperienceOfferPageActions } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import { addExperienceToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { addGTMEvent, fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { generateExperienceGiftCardItem } from 'lib/checkout/gift/cart'
import { FetchDateParams, getTotalSeats } from 'lib/experiences/experienceUtils'
import ExperienceTicketCustomerLimit from '../ExperienceTickets/ExperienceTicketCustomerLimit'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import usePrevious from 'hooks/usePrevious'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { addToUserCart } from 'actions/AuthActions'
import LineShoppingCartIcon from 'components/Luxkit/Icons/line/LineShoppingCartIcon'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { isMultiCartExperienceSupported } from 'lib/checkout/userCart'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { connect } from 'react-redux'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import PriceRowLuxPlusPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusNonMemberPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'

const TicketContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
`

const TicketContainerWithoutPadding = styled(TicketContainer)`
  padding: 0;
`

const TicketInputContainer = styled.div`
  padding: ${rem(24)};
`

const FadingPriceRowPriceCaption = styled(PriceRowPriceCaption)`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . ."
    "buy buy buy";
  grid-template-columns: auto 1fr auto;
  grid-row-gap: 12px;
  grid-column-gap: 20px;
  align-items: center;
  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
`
const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`
const BuyGridItem = styled.div`
  grid-area: buy;
`

type SubmitAction = 'buyNow' | 'keepBrowsing' | 'addToUserCart'
interface Props {
  experienceView: App.ExperienceBookingView;
  isBuyNowBookLater?: boolean;
  isGift?: boolean;
  luxPlusEnabled: boolean;
}

const ExperienceOfferPageTicketSelection = forwardRef<HTMLFormElement, Props>((props, formRef) => {
  const {
    experienceView,
    isBuyNowBookLater = false,
    isGift = false,
    luxPlusEnabled,
  } = props

  const internalFormRef = useRef<HTMLFormElement>(null)
  const [hasNoTicketsSelected, setHasNoTicketsSelected] = useState(false)
  useImperativeHandle(formRef, () => internalFormRef.current!)
  const currency = useContext(CurrencyContext)
  const dispatch = useAppDispatch()
  const experience = experienceView.originalExperience

  const userMultiItemCartEnabled = useAppSelector((state: App.State) => isMultiCartEnabled(state))
  const userMultiItemCartSupported = isMultiCartExperienceSupported(experience)

  const trackEvent = useTrackExperienceOfferEvent()

  const handleOptionPurchaseEvent = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_BUY_NOW_CLICKED')
    }
    if (isGift) {
      return trackEvent('GIFT_BUY_NOW_CLICKED')
    }
    return trackEvent('BUY_NOW_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [isBuyNowBookLater, isGift])

  const onTicketCountIncrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_INCREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackEvent('GIFT_INCREASE_OPTION_SELECTED')
    }
    return trackEvent('INCREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const onTicketCountDecrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_DECREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackEvent('GIFT_DECREASE_OPTION_SELECTED')
    }
    return trackEvent('DECREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const onTicketCountChange = useCallback((increment: boolean) => {
    if (increment) {
      return onTicketCountIncrease()
    }
    return onTicketCountDecrease()
  }, [onTicketCountDecrease, onTicketCountIncrease])

  const pageState = useContext(ExperiencePageStateContext)
  const { date, time, pickupPoint: selectedPickupPoint, ticketCounts, redemptionLocation: selectedLocation, language: selectedLanguage, ticketMode: selectedTicketMode } = pageState
  const experienceDispatch = useContext(ExperiencePageDispatchContext)

  const setTicketCounts = useCallback((ticketCounts: Record<string, number>) => experienceDispatch({ type: ExperienceOfferPageActions.setTicketCounts, ticketCounts }), [experienceDispatch])

  const setDate = useCallback((date?: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setDate, date })
  }, [experienceDispatch])

  const setSelectedPickupPoint = useCallback((pickupPoint?: App.ExperiencePickupPoint) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setPickupPoint, pickupPoint })
  }, [experienceDispatch])

  const setTimeSlot = useCallback((time: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTime, time })
  }, [experienceDispatch])

  const setSelectedLocation = useCallback((redemptionLocation?: App.ExperienceRedemptionLocation) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setRedemptionLocation, redemptionLocation })
  }, [experienceDispatch])

  const setLanguage = useCallback((language: App.ExperienceLanguage) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setLanguage, language })
  }, [experienceDispatch])

  const setSelectedTicketMode = useCallback((ticketMode?: App.ExperienceTicketMode) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTicketMode, ticketMode })
  }, [experienceDispatch])

  const fetchDateParams = useMemo(() => ({
    pickupPointId: selectedPickupPoint?.id,
    redemptionLocationId: selectedLocation?.id,
    ticketModeKey: selectedTicketMode?.key,
    isBuyNowBookLater,
    isGift,
  }), [selectedPickupPoint, selectedLocation, selectedTicketMode, isBuyNowBookLater, isGift])
  const [availableDates, fetchingDates, datesError] = useExperienceAvailableDates(experienceView.originalExperience, fetchDateParams)

  const fetchTimeParams = useMemo(() => ({
    date,
    redemptionLocationId: selectedLocation?.id,
    pickupPointId: selectedPickupPoint?.id,
    isBuyNowBookLater,
    isGift,
    ticketModeKey: selectedTicketMode?.key,
  }), [selectedPickupPoint, selectedLocation, selectedTicketMode, isBuyNowBookLater, isGift, date])
  const [availableTimes, fetchingTimeSlots, timeSlotsError] = useExperienceAvailableTimes(experienceView.originalExperience, fetchTimeParams)

  const loading = !!(fetchingDates || fetchingTimeSlots)

  const previousAvailableDates = usePrevious(availableDates)

  const loadDates = useCallback(() => {
    if (!experience) return

    const fetchParams: FetchDateParams = {}

    const locations = experience.redemptionLocations
    if (locations?.length === 1) {
      const defaultLocation = locations[0]
      setSelectedLocation(defaultLocation)
      fetchParams.redemptionLocationId = defaultLocation.id
      trackEvent('REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE')
    }

    const pickups = experience.pickupPoints
    if (pickups?.length === 1) {
      const defaultPickup = pickups[0]
      setSelectedPickupPoint(defaultPickup)
      fetchParams.pickupPointId = defaultPickup.id
      trackEvent('PICKUP_LOCATION_SELECTED_NON_INTERACTION')
    }

    const ticketModes = experience.ticketModes
    if (ticketModes?.length === 1) {
      const defaultTicketMode = ticketModes[0]
      setSelectedTicketMode(defaultTicketMode)
      fetchParams.ticketModeKey = defaultTicketMode.key
      trackEvent('TICKET_MODE_SELECTED_NON_INTERACTIVE')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience, isBuyNowBookLater, isGift, selectedPickupPoint, date, setSelectedPickupPoint, trackEvent, setSelectedLocation, setSelectedTicketMode])

  useEffect(() => {
    loadDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView])

  useEffect(() => {
    if (availableDates?.length && !experience.hasCalendar) {
      // we still fetch calendar data even though it doesn't have it as we need it
      // to go fetch the times which gives us the tickets
      const day = isBuyNowBookLater || isGift ? undefined : availableDates[0].day
      setDate(day)
      trackEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
    }
  }, [availableDates]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // After login the availableDates and availableTimes gets cleared
    // in the context, this will fire a refetch of dates and times
    // to update the limits for the new customer
    if (previousAvailableDates?.length && !availableDates?.length) {
      loadDates()
    }
  }, [previousAvailableDates, availableDates, loadDates])

  const onPickupPointSelect = useCallback((newPickupPoint?: App.ExperiencePickupPoint) => {
    if (newPickupPoint && selectedPickupPoint && newPickupPoint.id === selectedPickupPoint.id) return

    setSelectedPickupPoint(newPickupPoint)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedPickupPoint, selectedPickupPoint, setDate, setTimeSlot, setTicketCounts])

  const onLocationSelect = useCallback((newLocation?: App.ExperienceRedemptionLocation) => {
    if (newLocation && selectedLocation && newLocation.id === selectedLocation.id) return
    setSelectedLocation(newLocation)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedLocation, selectedLocation, setDate, setTimeSlot, setTicketCounts])

  const onTicketModeSelect = useCallback((newTicketMode?: App.ExperienceTicketMode) => {
    if (newTicketMode && selectedTicketMode && newTicketMode.key === selectedTicketMode.key) return

    setSelectedTicketMode(newTicketMode)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedTicketMode, selectedTicketMode, setDate, setTimeSlot, setTicketCounts])

  const onDateSelect = useCallback((newDate: App.ExperienceAvailabilityDate) => {
    if (date === newDate.day) return

    setDate(newDate.day)
    setTimeSlot('')
    setTicketCounts({})
  }, [setDate, date, setTimeSlot, setTicketCounts])

  const onSelectTimeSlot = useCallback((newTimeSlot: string) => {
    if (newTimeSlot === time) return
    setTimeSlot(newTimeSlot)
    setTicketCounts({})
  }, [setTimeSlot, time, setTicketCounts])

  const handleTicketCountChange = useCallback((ticket: App.ExperienceBookingView['ticketOptions'][number], nextCount: number) => {
    if (hasNoTicketsSelected) setHasNoTicketsSelected(false)
    setTicketCounts({
      ...ticketCounts,
      [ticket.id]: nextCount,
    })
    onTicketCountChange((ticketCounts[ticket.id] ?? 0) < nextCount)
  }, [setTicketCounts, ticketCounts, onTicketCountChange, hasNoTicketsSelected])

  const allTicketOptions = useExperienceTicketsOptions(availableTimes, { isBuyNowBookLater, isGift, time })

  const counts = useMemo(() => {
    return Object.entries(ticketCounts).map(([ticketId, count]) => {
      return {
        ticket: allTicketOptions.find(t => t.id === ticketId)!,
        count,
      }
      // sometimes the tickets we previously selected are no longer available
      // remove them
    }).filter(count => !!count.ticket)
  }, [ticketCounts, allTicketOptions])

  const { totalNonExtraCountOnCart, totalCountOnCart } = useMemo(() => ({
    totalCountOnCart: sum(counts, count => count.count),
    totalNonExtraCountOnCart: sum(counts.filter(item => !item.ticket.isExtra), count => count.count),
  }), [counts])

  const [isFromPrice, totalPrice] = useMemo(() => {
    const cheapestTicket = min(allTicketOptions, (ticket) => ticket.price)
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.price ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return [
      selectedPrice === 0,
      selectedPrice || experienceView.price || cheapestTicket?.price || 0,
    ]
  }, [ticketCounts, experienceView, allTicketOptions])

  const totalLuxPlusPrice = useMemo(() => {
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.memberPrice ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return selectedPrice || experience.pricing.memberPrice
  }, [ticketCounts, allTicketOptions, experience.pricing.memberPrice])

  const orderMode = useMemo<App.ExperienceOrderMode>(() => {
    switch (true) {
      case isGift && experience.giftType === 'offer':
        return 'gift-offer'
      case isGift && experience.giftType === 'gift-card':
        return 'gift-card'
      case isBuyNowBookLater:
        return 'bnbl'
      default:
        return 'booking'
    }
  }, [isGift, isBuyNowBookLater, experience.giftType])

  const generateCartItem = useCallback((ticketData: ExperienceDetailsForm) => {
    if (isGift && experience.giftType === 'gift-card') {
      return generateExperienceGiftCardItem(
        experience,
        totalPrice,
        counts,
      )
    } else {
      return generateExperienceCheckoutItem(
        experience,
        ticketData,
        counts,
        {
          isBuyNowBookLater,
          isGift,
          orderMode,
        },
      )
    }
  }, [counts, experience, isBuyNowBookLater, isGift, totalPrice, orderMode])

  const appendToUserCart = useCallback((ticketData: ExperienceDetailsForm) => {
    const item = generateCartItem(ticketData)
    dispatch(addToUserCart([item]))
    Analytics.trackEvent(addToCartMultiItem([item], { isMultiCart: true }))
  }, [dispatch, generateCartItem])

  const proceedToCheckout = useCallback((ticketData: ExperienceDetailsForm, keepBrowsing = false) => {
    const item = generateCartItem(ticketData)
    dispatch(addToCheckoutCart([item], isGift ? { isGift } : undefined))
    Analytics.trackEvent(addToCart())
    if (keepBrowsing) {
      showSnackbar('Your item has been added to the cart.', 'success')
      return
    }
    dispatch(navigateToCheckout())
  }, [generateCartItem, dispatch, isGift])

  const submitForm = useCallback((submitAction: SubmitAction) => {
    if (!internalFormRef.current?.reportValidity()) return
    if (totalNonExtraCountOnCart === 0 || minSeats > totalUsedSeatsOnCart) {
      setHasNoTicketsSelected(true)
      return
    }
    handleOptionPurchaseEvent()
    const ticketData = formToObject<ExperienceDetailsForm>(internalFormRef.current)
    delete ticketData.ticketCount // Unneeded field
    counts.map(ticket => {
      addGTMEvent(addExperienceToCheckout(experienceView, ticket, currency))
    })

    const generatedTicketData = isBuyNowBookLater || isGift ?
        {
          date: undefined,
          language: selectedLanguage?.id,
          pickupPoint: selectedPickupPoint?.id,
          redemptionLocation: selectedLocation?.id,
          ticketMode: selectedTicketMode?.key,
          time: ticketData.time,
        } : ticketData

    if (submitAction === 'addToUserCart') {
      appendToUserCart(generatedTicketData)
    } else {
      proceedToCheckout(generatedTicketData, submitAction === 'keepBrowsing')
    }

    const ticketQuantity = sum(counts ?? [], count => count.count)
    fireEcommerceGTMEvent(addToCartGTM(experience, currency, totalPrice, ticketQuantity, 'experience'))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onBuyNow is stable
  }, [counts, experience, currency, totalPrice, experienceView, isBuyNowBookLater, isGift, proceedToCheckout, selectedLanguage?.id, selectedLocation?.id, selectedPickupPoint?.id])

  const handleBuyNow = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('buyNow')
  }, [submitForm])

  const handleKeepBrowsing = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('keepBrowsing')
  }, [submitForm])

  const handleAddToUserCart = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('addToUserCart')
  }, [submitForm])

  const userSelectedFields = useMemo(() => {
    const fields: Array<string> = []
    if (experience.redemptionLocations.length > 1 || experience.pickupPoints.length > 1) {
      fields.push('location')
    }
    if (experience.hasCalendar && !datesError) {
      fields.push('date')

      if (experience.hasTimeslots && !timeSlotsError) {
        fields.push('time')
      }
    }
    if (experience.languages.length > 1) {
      fields.push('language')
    }
    return fields
  }, [datesError, timeSlotsError, experience])

  const currentTimeSlot = useMemo(() => {
    if (availableTimes) {
      if (isBuyNowBookLater || isGift) {
        let timeslots = availableTimes.filter(slot => slot.purchaseLimit.maxReason !== 'AVAILABILITY')
        if (!timeslots.length) timeslots = availableTimes

        return max<App.ExperienceAvailabilityTimeSlot>(timeslots, (slot => slot.purchaseLimit.max))
      } else {
        return availableTimes.find(slot => slot.time === time)
      }
    }
  }, [availableTimes, time, isBuyNowBookLater, isGift])

  const { totalUsedSeatsOnCart, totalSeatsRemaining } = useMemo(() => {
    if (!currentTimeSlot) return { totalSeatsRemaining: 0, totalUsedSeatsOnCart: 0, totalUsedSeatsOnServer: 0 }
    return getTotalSeats(currentTimeSlot, ticketCounts)
  }, [ticketCounts, currentTimeSlot])

  const hasError = datesError || timeSlotsError
  const emptyTimeSlots = !!(date && !availableTimes.length)
  // for voucher based sometimes some tickets or all tickets will be sold out
  const offerWithAllTicketsSoldOut = emptyTimeSlots && !hasError && !loading

  const minSeats = currentTimeSlot?.purchaseLimit.min ?? 1

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])
  const showLuxPlusPrice = useMemo(() => {
    return luxPlusEnabled && experience.pricing.memberPrice > 0 && experience.giftType !== 'gift-card'
  }, [luxPlusEnabled, experience])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (isFromPrice) {
      return [{
        // if lux plus is enabled, we need to use the member price
        price: showLuxPlusPrice ? totalLuxPlusPrice : totalPrice,
        loyaltyProductType: 'experience',
      }]
    }

    const ticketsById = arrayToObject(allTicketOptions, ticket => ticket.id)

    const selectedTickets = Object.entries(ticketCounts).flatMap(([id, count]): Array<App.LoyaltyPointsEarnCalculationRequest> => {
      const ticket = ticketsById[id]
      if (!ticket) return []
      return Array.from({ length: count }, (): App.LoyaltyPointsEarnCalculationRequest => ({
        price: showLuxPlusPrice ? ticket.memberPrice || 0 : ticket.price || 0,
        loyaltyProductType: 'experience',
      }))
    })

    return selectedTickets
  }, [allTicketOptions, ticketCounts, totalPrice, isFromPrice, showLuxPlusPrice, totalLuxPlusPrice])

  return (<VerticalSpacer as="form" gap={20} ref={internalFormRef}>
    {!isBuyNowBookLater && !isGift &&
      <TicketContainerWithoutPadding>
        <LoadingIndicator floating visible={loading} />
        <TicketInputContainer>
          <ExperienceTicketInputs
            experienceView={experienceView}
            onTimeSelect={onSelectTimeSlot}
            onPickupPointSelect={onPickupPointSelect}
            onLocationSelect={onLocationSelect}
            onTicketModeSelect={onTicketModeSelect}
            allDates={availableDates}
            selectedDate={date ?? ''}
            selectedTimeSlot={time ?? ''}
            defaultLanguageId={selectedLanguage?.id}
            defaultPickupPointId={selectedPickupPoint?.id}
            defaultRedemptionLocationId={selectedLocation?.id}
            availableTimes={availableTimes}
            onDateSelect={onDateSelect}
            onLanguageSelect={setLanguage}
          />
          {(hasError || offerWithAllTicketsSoldOut) && <BodyText variant="large" colour="critical">
            There are no tickets available for your selection
            {userSelectedFields.length > 0 ? `, try another ${arrayToStringListFormat(userSelectedFields)}` : ''}
            .
          </BodyText>}
        </TicketInputContainer>
        {allTicketOptions.length > 0 && !!currentTimeSlot && <>
          <ExperienceTicketCustomerLimit
            purchaseLimit={currentTimeSlot.purchaseLimit}
            totalSeatsRemaining={totalSeatsRemaining}
            totalUsedSeatsOnCart={totalUsedSeatsOnCart}
          />
          {(totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected && (
            <MessageBanner
              kind="warning"
              description="You should add at least one experience ticket"
              icon={<LineInfoCircleIcon />}
            />
          )}
        </>}

      </TicketContainerWithoutPadding>
    }
    <LoadingIndicator floating visible={(isBuyNowBookLater || isGift) && loading} />
    {(isBuyNowBookLater || isGift) && ((totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected) && (
      <MessageBanner
        kind="warning"
        description="You should add at least one experience ticket"
        icon={<LineInfoCircleIcon />}
      />
    )}
    {allTicketOptions.length > 0 && <TicketContainerWithoutPadding>
      <LoadingIndicator floating visible={(!isBuyNowBookLater || !isGift) && loading} />
      <TicketInputContainer>
        <ExperienceTicketSelection
          ticketOptions={allTicketOptions}
          ticketCounts={ticketCounts}
          onTicketCountChange={handleTicketCountChange}
          purchaseLimitRemaining={totalSeatsRemaining}
          purchaseLimitReason={currentTimeSlot?.purchaseLimit.maxReason}
          orderMode={orderMode}
        />
      </TicketInputContainer>
      {(isBuyNowBookLater || isGift) && !!currentTimeSlot && <ExperienceTicketCustomerLimit
        purchaseLimit={currentTimeSlot.purchaseLimit}
        totalSeatsRemaining={totalSeatsRemaining}
        totalUsedSeatsOnCart={totalUsedSeatsOnCart}
      />}
    </TicketContainerWithoutPadding>}
    <BottomContent>
      <PriceGridItem>
        <Group direction="vertical" gap={4}>
          {!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={cn({ visible: isFromPrice })}>
                From
              </FadingPriceRowPriceCaption>
              <PriceRowPrice
                size="M"
                price={totalPrice}
                saleUnit={(experience.ticketUnitLabel && isFromPrice) ? experience.ticketUnitLabel : 'total'}
              />
              <PriceRowAgentHubCommission
                offerId={experience.id}
                productType={productType}
                offerLocations={[experience.location.name]}
                size="L"
              />
            </Group>
          )}
          {!!showLuxPlusPrice && (
            <Group direction="vertical">
              <FadingPriceRowPriceCaption className={cn({ visible: isFromPrice })}>
                From
              </FadingPriceRowPriceCaption>
              <PriceRowLuxPlusPrice
                logoPlacement="trailing"
                size="L"
                memberPrice={totalLuxPlusPrice}
                saleUnit={experience.ticketUnitLabel && isFromPrice ? experience.ticketUnitLabel : 'total'}
              />
              <PriceRowLuxPlusNonMemberPrice
                size="L"
                nonMemberPrice={totalPrice}
              />
            </Group>
          )}
          <LoyaltyPoints
            calculationRequests={pointsEarnCalculationRequest}
            calculationType={isFromPrice ? 'estimate' : 'exact'}
            hideStatusCredits
          />
        </Group>
      </PriceGridItem>
      <TripGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </TripGuard>
      <BuyGridItem>
        <Group direction="vertical-reverse" gap={8} tabletDirection="horizontal">
          {userMultiItemCartEnabled && userMultiItemCartSupported && <TextButton
            kind="secondary"
            startIcon={<LineShoppingCartIcon />}
            size="large"
            onClick={handleAddToUserCart}
            disabled={experience.status !== 'ONLINE'}
          >
            Add to cart
          </TextButton>}
          <TextButton
            kind="primary"
            size="large"
            type="submit"
            onClick={handleBuyNow}
            disabled={experience.status !== 'ONLINE'}
          >
            Buy now
          </TextButton>
          <CSSBreakpoint min="desktop">
            {isAgentHubMultiCartItemsEnabled && !userMultiItemCartEnabled && (
              <TextButton
                kind="secondary"
                size="large"
                type="submit"
                onClick={handleKeepBrowsing}
                disabled={experience.status !== 'ONLINE'}
              >
                Add to cart
              </TextButton>
            )}
          </CSSBreakpoint>
        </Group>
      </BuyGridItem>
    </BottomContent>
  </VerticalSpacer>
  )
})

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(React.memo(ExperienceOfferPageTicketSelection))
